<script>
// Balão do tour guiado: título, detalhe, progresso e as ações do passo.
// Puramente apresentacional — não resolve alvo, não conhece a Jornada.
//
// Publica a própria dimensão medida por `@medido`. O host posiciona com uma
// estimativa, o card mede no $nextTick e o host reposiciona: é o que permite
// o balão crescer com o texto sem ninguém adivinhar altura. O `Tour` legado
// tem `cardWidth: 260 / cardHeight: 180` cravados no cálculo de clamp, e um
// detalhe mais longo que isso vaza da viewport em silêncio.

const FASE_DESFECHO = 'desfecho'

// Copy da espera. Diz o que destrava, não "aguarde" — o usuário precisa saber
// que a bola está com ele.
const TEXTO_AGUARDANDO = 'Conclua a ação para seguir'

export default {
  name: 'GuidedTourCard',

  props: {
    titulo: {
      type: String,
      default: '',
    },

    detalhe: {
      type: String,
      default: '',
    },

    // 0-based, como o índice em instructions[].
    indice: {
      type: Number,
      default: 0,
    },

    total: {
      type: Number,
      default: 0,
    },

    fase: {
      type: String,
      default: 'passos',
    },

    // { rotulo } da etapa seguinte, ou null quando não há próxima guiável.
    proximaEtapa: {
      type: Object,
      default: null,
    },

    // Checkbox "marcar concluída" no desfecho (v-model).
    // Default `false` por exigência do lint (vue/no-boolean-default) — quem
    // manda de verdade é o slice do tour, que nasce com a marcação LIGADA.
    marcarConcluida: {
      type: Boolean,
      default: false,
    },

    marcando: {
      type: Boolean,
      default: false,
    },

    // O drawer de texto da Jornada está expandido? Só muda o rótulo do botão —
    // quem sabe do drawer é a casca de integração, não este card.
    // Default `false` por exigência do lint (vue/no-boolean-default).
    drawerAberto: {
      type: Boolean,
      default: false,
    },

    // Este passo termina quando o APP avisar (o passo tem `avancaEm`), não no
    // clique do usuário. Troca o "Próximo" por uma linha de espera — ver o
    // comentário no template.
    // Default `false` por exigência do lint (vue/no-boolean-default).
    aguardaAcao: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      medidaAnterior: null,
      TEXTO_AGUARDANDO,
    }
  },

  computed: {
    noDesfecho() {
      return this.fase === FASE_DESFECHO
    },

    ehPrimeiroPasso() {
      return this.indice <= 0
    },

    rotuloDoProgresso() {
      if (!this.total) return ''
      return `Passo ${this.indice + 1} de ${this.total}`
    },

    // O host pode não ter próxima etapa guiável (fim do escopo) — aí o
    // desfecho é só um encerramento, sem oferta.
    temProxima() {
      return !!(this.proximaEtapa && this.proximaEtapa.rotulo)
    },

    rotuloDaProxima() {
      if (!this.temProxima) return ''
      return `Ir para: ${this.proximaEtapa.rotulo}`
    },

    // Desfecho empilha as ações em coluna, largura cheia. Em linha, "Ir para:
    // Cadastrar cliente" (175px) + gap + "Encerrar por aqui" (145px) = 328px
    // contra 288px úteis: o primário quebrava o texto e o "Encerrar" caía
    // sozinho numa linha abaixo (medido em tela). Rótulos do Fee Fixo chegam a
    // "Ir para: Acompanhar onboarding pós-conversão" — em 288px ainda leem bem.
    // Passos seguem em linha, à direita. O layout vive na regra escopada do
    // modificador (ver `<style>`), não em utilitário: `flex-column` não tem
    // efeito no runtime deste app (medido: flex-direction computado = row).
    classesDasAcoes() {
      if (!this.noDesfecho) return []
      return ['gt-card__acoes--desfecho']
    },

    rotuloDoDrawer() {
      return this.drawerAberto ? 'Ocultar passo a passo' : 'Ver passo a passo'
    },

    pontos() {
      const lista = []
      for (let i = 0; i < this.total; i += 1) {
        lista.push({ indice: i, ativo: i === this.indice, feito: i < this.indice })
      }
      return lista
    },
  },

  watch: {
    titulo: 'publicarMedida',
    detalhe: 'publicarMedida',
    fase: 'publicarMedida',
  },

  mounted() {
    this.publicarMedida()
  },

  methods: {
    // Só emite quando a dimensão MUDA: o host reposiciona ao receber, o que
    // re-renderiza este card — emitir sempre fecharia o laço.
    publicarMedida() {
      this.$nextTick(() => {
        if (!this.$el || !this.$el.getBoundingClientRect) return
        const r = this.$el.getBoundingClientRect()
        const medida = { width: r.width, height: r.height }
        const igual =
          this.medidaAnterior &&
          this.medidaAnterior.width === medida.width &&
          this.medidaAnterior.height === medida.height
        if (igual) return
        this.medidaAnterior = medida
        this.$emit('medido', medida)
      })
    },

    aoAlternarMarcacao(valor) {
      this.$emit('update:marcarConcluida', !!valor)
    },
  },
}
</script>

<template>
  <dialog class="gt-card" open aria-live="polite" :aria-label="titulo">
    <div class="gt-card__topo">
      <span v-if="!noDesfecho" class="gt-card__progresso az-ds-aux">
        {{ rotuloDoProgresso }}
      </span>
      <span v-else class="gt-card__progresso az-ds-aux">Etapa concluída</span>

      <div class="gt-card__topo-acoes">
        <!-- As duas apresentações da mesma etapa convivem: o spotlight mostra
             ONDE clicar, o drawer mostra a etapa inteira em texto. Quem só quer
             ler continua tendo por onde — sem ter de encerrar o tour para isso.
             Só faz sentido com passos; no desfecho não há passo a passo a ler. -->
        <button
          v-if="!noDesfecho"
          type="button"
          class="gt-card__passo-a-passo az-ds-aux"
          :aria-label="rotuloDoDrawer"
          @click="$emit('alternar-drawer')"
        >
          {{ rotuloDoDrawer }}
        </button>

        <button
          type="button"
          class="gt-card__fechar"
          aria-label="Encerrar a jornada guiada"
          @click="$emit('fechar')"
        >
          <icon-svg name="close" size="12" color="var(--neutral-600)" />
        </button>
      </div>
    </div>

    <!-- `az-ds-card-title`, NÃO `az-ds-section-title`: mesma métrica (16px/700),
         sem o `text-transform: uppercase`. Em caixa alta o título de 16px bold
         lia como grito dentro de um balão pequeno — "parece que está gritando"
         (owner, 14/08). Trocar a classe do DS resolve sem CSS ad-hoc, que o gate
         de design reprovaria e que divergiria do token na próxima revisão. -->
    <h3 class="gt-card__titulo az-ds-card-title">{{ titulo }}</h3>

    <p v-if="detalhe" class="gt-card__detalhe az-ds-body">{{ detalhe }}</p>

    <div v-if="noDesfecho" class="gt-card__marcar">
      <input-by-type
        type="checkbox"
        :value="marcarConcluida"
        label="Marcar esta etapa como concluída"
        hide-details
        @input="aoAlternarMarcacao"
      />
    </div>

    <div v-if="!noDesfecho && total > 1" class="gt-card__pontos">
      <span
        v-for="ponto in pontos"
        :key="ponto.indice"
        class="gt-card__ponto"
        :class="{
          'gt-card__ponto--ativo': ponto.ativo,
          'gt-card__ponto--feito': ponto.feito,
        }"
      ></span>
    </div>

    <div class="gt-card__acoes" :class="classesDasAcoes">
      <template v-if="noDesfecho">
        <!-- Largura cheia vem de `.gt-card__acoes--desfecho > .btn` no <style>. -->
        <BaseButton
          v-if="temProxima"
          primary
          :loading="marcando"
          @click="$emit('proxima-etapa')"
        >
          {{ rotuloDaProxima }}
        </BaseButton>
        <!-- Sem próxima etapa (última da trilha), esta é a ÚNICA ação do
             desfecho e precisa do peso de primária: `underlined` ali deixava o
             balão sem nenhuma ação em destaque, parecendo que faltava algo a
             fazer (visto em tela, 14/08). Com próxima, segue secundária — o
             destaque é seguir, não parar. -->
        <BaseButton
          :primary="!temProxima"
          :underlined="temProxima"
          :loading="marcando && !temProxima"
          @click="$emit('fechar')"
        >
          Encerrar por aqui
        </BaseButton>
      </template>

      <template v-else>
        <BaseButton
          v-if="!ehPrimeiroPasso"
          underlined
          @click="$emit('anterior')"
        >
          Voltar
        </BaseButton>
        <!-- Passo que conclui pela AÇÃO REAL (`avancaEm`) não deixa o "Próximo"
             adiantar o balão: quem avança é o app, quando a ação terminar.
             Sem isto, o usuário clicava "Próximo" no passo de confirmar a
             importação, ia parar no passo 7 e lia "confira o que entrou" com o
             modal ainda aberto e nada gravado (relatado em tela, 14/08). Se o
             tour não pode fazer a ação por ele — porque ela GRAVA —, também não
             pode fingir que ela aconteceu.
             Saídas continuam abertas: "Voltar", o X e "Ver passo a passo". -->
        <span v-if="aguardaAcao" class="gt-card__aguardando az-ds-aux">
          {{ TEXTO_AGUARDANDO }}
        </span>
        <BaseButton v-else primary @click="$emit('proximo')">
          Próximo
        </BaseButton>
      </template>
    </div>
  </dialog>
</template>

<style lang="sass" scoped>
.gt-card
  // Reset do UA stylesheet do <dialog> nativo (Web:S6819): sem isto o browser
  // aplica position:absolute/inset:0/margin:auto/border:solid/color:black por
  // conta própria, e o card (que já é posicionado pelo wrapper `.gt-host__card`
  // via :style inline) some de onde deveria estar. `open` sem showModal() é o
  // que preserva o comportamento não-bloqueante de sempre — sem backdrop, sem
  // focus trap, sem top layer.
  position: static
  margin: 0
  border: none
  color: inherit
  overflow: visible
  width: 320px
  max-width: calc(100vw - 32px)
  background: var(--white)
  border-radius: var(--radius-md)
  padding: 16px
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.24)
  // O host portaliza e posiciona; o card só precisa voltar a receber cliques
  // (o root do overlay é pointer-events:none para não bloquear a tela).
  pointer-events: auto

  &__topo
    display: flex
    align-items: center
    justify-content: space-between
    margin-bottom: 8px

  &__progresso
    color: var(--neutral-600)
    text-transform: uppercase
    letter-spacing: 0.04em

  &__topo-acoes
    display: flex
    align-items: center
    gap: 8px

  // Ocupa o lugar do "Próximo" sem o peso de botão: não há o que clicar aqui,
  // a ação está na tela atrás do balão.
  &__aguardando
    color: var(--neutral-600)
    align-self: center

  // Peso de link, não de botão: é uma alternativa de leitura, não a ação do
  // passo — competir com "Próximo" tiraria o olho de onde o tour quer levar.
  &__passo-a-passo
    color: var(--primary)
    background: transparent
    text-decoration: underline
    white-space: nowrap

    &:hover
      color: var(--primary-dark)

  &__fechar
    display: flex
    align-items: center
    justify-content: center
    width: 24px
    height: 24px
    border-radius: var(--radius-sm)
    background: transparent

    &:hover
      background: var(--neutral-100)

  &__titulo
    margin: 0 0 6px
    color: var(--neutral-900)

  &__detalhe
    margin: 0
    color: var(--neutral-600)
    // Detalhe do catálogo chega a ~260 caracteres; sem teto o balão empurra
    // as ações para fora da viewport em telas baixas.
    max-height: 168px
    overflow-y: auto
    // Absorve a sobra sub-pixel do arredondamento das linhas: com line-height
    // 16.1px, 2 linhas dão 32.2px e o browser media scrollHeight 33 contra
    // clientHeight 32 — e o `overflow-y: auto` desenhava scrollbar num texto
    // que cabe. 2px (não 1) cobre zoom < 100%, onde 1px de device vale mais
    // que 1px CSS. O teto de 168px segue rolando textos longos de verdade.
    padding-bottom: 2px

  &__marcar
    margin-top: 12px

  &__pontos
    display: flex
    gap: 4px
    margin-top: 14px

  &__ponto
    width: 6px
    height: 6px
    border-radius: 50%
    background: var(--neutral-300)

    &--feito
      background: var(--neutral-400)

    &--ativo
      background: var(--primary)
      width: 16px
      border-radius: 3px

  &__acoes
    display: flex
    align-items: center
    justify-content: flex-end
    gap: 8px
    margin-top: 16px
    flex-wrap: wrap

    // Desfecho: ações empilhadas, largura cheia (288px úteis). Utilitários
    // não resolveram (medido no navegador): `flex-column` ficava com
    // flex-direction = row neste runtime, e `fit-content` + `w-100` davam
    // 197px/145px porque `.btn-fit-content { max-width: fit-content
    // !important }` (`_btn.sass`) vence o width:100%; sem `fit-content` volta
    // o `max-width: 175px` do `.btn`. O BaseButton renderiza o <button.btn>
    // como raiz, então `> .btn` o alcança sem ::v-deep; a especificidade
    // (0,3,0) supera `.btn:not(.btn-fit-content)` e `.btn + .btn` (0,2,0).
    &--desfecho
      flex-direction: column
      align-items: stretch

      > .btn
        width: 100%
        max-width: none
        margin-left: 0
</style>
